import React, { useCallback, useEffect, useState } from "react";
import moment from "moment";
import classNames from "classnames";
import {
  getUserInstitutions,
  getUserOrders,
  getUserTransactions,
} from "../../services/adminMarketplaceService";
import {
  getBranchLoyaltyCards,
  GalenLoyaltyPage,
} from "../../services/galenLoyaltyService";
import { MarketplaceResponse } from "../../types/shared";
import {
  CnCOrderResponse,
  CnCTransactionResponse,
  CnCOrderStatus,
} from "../../types/api";
import { useSelector } from "react-redux";
import { RootState } from "../../store";
import { getTokenUserId } from "../../utils/tokenUtils";
import styles from "./AdminMarketPlacePage.module.scss";
import Select, { SingleValue } from "react-select";
import Calendar from "react-calendar";

type View = "orders" | "transactions" | "loyalty";

// Statusi koji predstavljaju potencijalni prihod od rezervacija
// kreirano/spremno su porudžbine u toku, propušteno je izgubljena prilika.
const POTENTIAL_RESERVATION_STATUSES: CnCOrderStatus[] = [
  "CREATED",
  "PREPARED",
  "SUSPENDED",
];

// Statusi koji predstavljaju realizovan prihod od transakcija
// Samo stvarno preuzete porudžbine.
const REALIZED_TRANSACTION_STATUSES: CnCOrderStatus[] = ["TAKEN"];

interface DateFilterModalProps {
  initialDateFrom: string;
  initialDateTo: string;
  onApply: (from: string, to: string) => void;
  onCancel: () => void;
}

const DateFilterModal: React.FC<DateFilterModalProps> = ({
  initialDateFrom,
  initialDateTo,
  onApply,
  onCancel,
}) => {
  const [dateRange, setDateRange] = useState<[Date, Date]>([
    moment(initialDateFrom).toDate(),
    moment(initialDateTo).toDate(),
  ]);
  const [hasSelectedNewRange, setHasSelectedNewRange] = useState(false);

  const handleDateChange = (val: unknown) => {
    if (Array.isArray(val) && val.length === 2) {
      setDateRange(val as [Date, Date]);
      setHasSelectedNewRange(true);
    }
  };

  const handleApply = () => {
    onApply(
      moment(dateRange[0]).format("YYYY-MM-DD"),
      moment(dateRange[1]).format("YYYY-MM-DD"),
    );
  };

  return (
    <div className={styles.modalOverlay} onClick={onCancel}>
      <div className={styles.filterModal} onClick={(e) => e.stopPropagation()}>
        <h3 className={styles.modalTitle}>Izaberite period</h3>

        <Calendar
          className={styles.filterCalendar}
          selectRange={true}
          returnValue="range"
          value={dateRange}
          onChange={handleDateChange}
          locale="sr"
          maxDate={new Date()}
        />

        <div className={styles.selectedRange}>
          <span>Od: {moment(dateRange[0]).format("DD.MM.YYYY")}</span>
          <span>Do: {moment(dateRange[1]).format("DD.MM.YYYY")}</span>
        </div>

        <div className={styles.modalActions}>
          <button
            type="button"
            className={styles.cancelButton}
            onClick={onCancel}
          >
            Otkaži
          </button>
          <button
            type="button"
            className={styles.applyButton}
            onClick={handleApply}
            disabled={!hasSelectedNewRange}
          >
            Primeni
          </button>
        </div>
      </div>
    </div>
  );
};

const LOYALTY_PAGE_SIZE = 20;

const instKey = (m?: {
  marketplaceId?: number;
  branchMarketplaceId?: number;
}) => (m ? `${m.marketplaceId}-${m.branchMarketplaceId}` : "");

const fmtDate = (iso?: string) =>
  iso ? moment(iso).format("DD.MM.YYYY. HH:mm") : "-";

const fmtMoney = (n?: number) =>
  typeof n === "number" ? `${n.toFixed(2)} RSD` : "-";

const statusLabel: Record<CnCOrderStatus, string> = {
  CREATED: "Kreirano",
  PREPARED: "Pripremljeno",
  SEEN: "Pregledano",
  PROCESSED: "Obrađeno",
  CANCELED: "Otkazano",
  REJECTED: "Odbijeno",
  TAKEN: "Preuzeto",
  NOT_TAKEN: "Nije preuzeto",
  FOR_DELIVERY: "Za predaju",
  IN_DELIVERY: "Preuzelo ovlašćeno lice",
  SUSPENDED: "Propušteno",
};

const AdminMarketPlacePage = () => {
  const token = useSelector((state: RootState) => state.auth.token);
  const userId = token ? getTokenUserId(token) : null;

  const [institutions, setInstitutions] = useState<MarketplaceResponse[]>([]);
  const [orders, setOrders] = useState<CnCOrderResponse[]>([]);
  const [transactions, setTransactions] = useState<CnCTransactionResponse[]>(
    [],
  );

  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [view, setView] = useState<View>("orders");
  const [from, setFrom] = useState(
    moment().subtract(7, "days").format("YYYY-MM-DD"),
  );
  const [to, setTo] = useState(moment().format("YYYY-MM-DD"));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loyalty, setLoyalty] = useState<GalenLoyaltyPage | null>(null);
  const [loyaltyPage, setLoyaltyPage] = useState(0);
  const [loadingLoyalty, setLoadingLoyalty] = useState(false);
  const [loyaltyError, setLoyaltyError] = useState<string | null>(null);
  const [loyaltyReloadKey, setLoyaltyReloadKey] = useState(0);
  const [showCalendar, setShowCalendar] = useState(false);
  const [isFilterApplied, setIsFilterApplied] = useState(false);

  const branchIds = Array.from(
    new Set(institutions.map((m) => m.branchMarketplaceId)),
  );
  const currentBranchId = selectedKey
    ? (institutions.find((m) => instKey(m) === selectedKey)
        ?.branchMarketplaceId ?? null)
    : branchIds.length === 1
      ? branchIds[0]
      : null;

  const loadData = useCallback(() => {
    if (!userId) return;
    setLoading(true);
    setError(null);
    Promise.allSettled([
      getUserInstitutions(userId),
      getUserOrders(userId),
      getUserTransactions(userId),
    ])
      .then(([inst, ord, tx]) => {
        if (inst.status === "fulfilled") setInstitutions(inst.value);
        if (ord.status === "fulfilled") setOrders(ord.value);
        if (tx.status === "fulfilled") setTransactions(tx.value);
        if ([inst, ord, tx].some((r) => r.status === "rejected")) {
          setError("Greška pri učitavanju podataka.");
        }
      })
      .finally(() => setLoading(false));
  }, [userId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (view !== "loyalty" || currentBranchId === null) {
      setLoadingLoyalty(false);
      setLoyaltyError(null);
      return;
    }
    let active = true;
    setLoadingLoyalty(true);
    setLoyaltyError(null);
    setLoyalty(null);
    getBranchLoyaltyCards(currentBranchId, {
      from: from || undefined,
      to: to || undefined,
      page: loyaltyPage,
      size: LOYALTY_PAGE_SIZE,
      sort: "created,desc",
    })
      .then((d) => {
        if (active) setLoyalty(d);
      })
      .catch((e) => {
        if (active) {
          console.error("Greška pri učitavanju loyalty kartica:", e);
          setLoyaltyError("Greška pri učitavanju loyalty kartica.");
        }
      })
      .finally(() => {
        if (active) setLoadingLoyalty(false);
      });
    return () => {
      active = false;
    };
  }, [view, currentBranchId, loyaltyPage, from, to, loyaltyReloadKey]);

  const inRange = (iso?: string) => {
    const hasFilter = Boolean(from || to);
    const d = iso ? moment(iso) : null;
    if (!d || !d.isValid()) return !hasFilter;
    if (from && d.isBefore(moment(from), "day")) return false;
    if (to && d.isAfter(moment(to), "day")) return false;
    return true;
  };

  const visibleOrders = orders
    .filter(
      (o) =>
        (selectedKey === null ||
          instKey(o.marketplaceResponse) === selectedKey) &&
        inRange(o.created) &&
        POTENTIAL_RESERVATION_STATUSES.includes(o.status),
    )
    .sort((a, b) => moment(b.created).valueOf() - moment(a.created).valueOf());

  const visibleTransactions = transactions
    .filter(
      (t) =>
        (selectedKey === null ||
          instKey(t.marketplaceResponse) === selectedKey) &&
        inRange(t.created) &&
        REALIZED_TRANSACTION_STATUSES.includes(t.status),
    )
    .sort((a, b) => moment(b.created).valueOf() - moment(a.created).valueOf());

  const totalOrdersAmount = visibleOrders.reduce(
    (sum, o) => sum + (o.totalPrice || 0),
    0,
  );
  const totalTransactionsAmount = visibleTransactions.reduce(
    (sum, t) => sum + (t.totalPrice || 0),
    0,
  );

  const handleApplyDateFilter = (newFrom: string, newTo: string) => {
    setFrom(newFrom);
    setTo(newTo);
    setLoyaltyPage(0);
    setShowCalendar(false);
    setIsFilterApplied(true);
  };

  const handleResetPeriod = () => {
    setFrom(moment().subtract(7, "days").format("YYYY-MM-DD"));
    setTo(moment().format("YYYY-MM-DD"));
    setLoyaltyPage(0);
    setIsFilterApplied(false);
  };
  type InstOption = { value: string; label: string };
  const institutionOptions: InstOption[] = [
    { value: "", label: "Sve apoteke" },
    ...institutions.map((m) => ({
      value: instKey(m),
      label: `${m.name}${m.location ? `, ${m.location}` : ""}`,
    })),
  ];

  return (
    <div className={styles.page}>
      <h2 className={styles.title}>Pregled apoteka</h2>
      {error && (
        <div className={styles.errorBanner}>
          <span>{error}</span>
          <button onClick={loadData}>Pokušaj ponovo</button>
        </div>
      )}
      <div className={styles.filters}>
        <Select
          options={institutionOptions}
          value={
            institutionOptions.find((o) => o.value === (selectedKey ?? "")) ??
            institutionOptions[0]
          }
          onChange={(e: SingleValue<InstOption>) => {
            setSelectedKey(e?.value || null);
            setLoyaltyPage(0);
          }}
          placeholder="Izaberite apoteku"
          aria-label="Izaberite apoteku"
          menuPosition="fixed"
          className={styles.institutionSelect}
        />
        <button
          type="button"
          className={styles.periodButton}
          onClick={() => setShowCalendar(true)}
        >
          {moment(from).format("DD.MM.YYYY")} -{" "}
          {moment(to).format("DD.MM.YYYY")}
        </button>
        {isFilterApplied && (
          <button
            type="button"
            className={styles.clearPeriodButton}
            onClick={handleResetPeriod}
          >
            Poništi
          </button>
        )}
      </div>

      {showCalendar && (
        <DateFilterModal
          initialDateFrom={from}
          initialDateTo={to}
          onApply={handleApplyDateFilter}
          onCancel={() => setShowCalendar(false)}
        />
      )}

      <div className={styles.tabs} role="tablist">
        <button
          id="orders-tab"
          aria-controls="orders-panel"
          role="tab"
          aria-selected={view === "orders"}
          className={classNames(styles.tab, {
            [styles.tabActive]: view === "orders",
          })}
          onClick={() => setView("orders")}
        >
          Rezervacije ({visibleOrders.length})
        </button>
        <button
          role="tab"
          id="transactions-tab"
          aria-controls="transactions-panel"
          aria-selected={view === "transactions"}
          className={classNames(styles.tab, {
            [styles.tabActive]: view === "transactions",
          })}
          onClick={() => setView("transactions")}
        >
          Transakcije ({visibleTransactions.length})
        </button>
        <button
          role="tab"
          id="loyalty-tab"
          aria-controls="loyalty-panel"
          aria-selected={view === "loyalty"}
          className={classNames(styles.tab, {
            [styles.tabActive]: view === "loyalty",
          })}
          onClick={() => {
            setView("loyalty");
            setLoyaltyPage(0);
          }}
        >
          Loyalty kartice
        </button>
      </div>
      <div role="tabpanel" id={`${view}-panel`} aria-labelledby={`${view}-tab`}>
        {view === "loyalty" && (
          <div className={styles.loyaltyNote}>
            Loyalty kartice se prikazuju za celu ustanovu (ne po pojedinačnoj
            apoteci).
          </div>
        )}
        {loading ? (
          <div className={styles.empty}>Učitavanje...</div>
        ) : view === "orders" ? (
          <div className={styles.tableWrap}>
            <table>
              <thead>
                <tr>
                  <th>Br.</th>
                  <th>Apoteka</th>
                  <th>Korisnik</th>
                  <th>Telefon</th>
                  <th>Iznos</th>
                  <th>Status</th>
                  <th>Kreirano</th>
                </tr>
              </thead>
              <tbody>
                {visibleOrders.map((o) => (
                  <tr key={o.id}>
                    <td>{o.id}</td>
                    <td>{o.marketplaceResponse?.name}</td>
                    <td>
                      {o.firstName} {o.secondName}
                    </td>
                    <td>{o.userPhoneNumberForOrder}</td>
                    <td>{fmtMoney(o.totalPrice)}</td>
                    <td>{statusLabel[o.status] || o.status}</td>
                    <td>{fmtDate(o.created)}</td>
                  </tr>
                ))}
                {visibleOrders.length === 0 && (
                  <tr>
                    <td colSpan={7} className={styles.empty}>
                      Nema rezervacija
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            <div className={styles.totalAmount}>
              Potencijalni iznos rezervacija: {fmtMoney(totalOrdersAmount)}
            </div>
          </div>
        ) : view === "transactions" ? (
          <div className={styles.tableWrap}>
            <table>
              <thead>
                <tr>
                  <th>Br. rezervacije</th>
                  <th>Apoteka</th>
                  <th>Korisnik</th>
                  <th>Telefon</th>
                  <th>Iznos</th>
                  <th>Status</th>
                  <th>Kreirano</th>
                </tr>
              </thead>
              <tbody>
                {visibleTransactions.map((t) => (
                  <tr key={t.id}>
                    <td>{t.orderNumber}</td>
                    <td>{t.marketplaceResponse?.name}</td>
                    <td>
                      {t.firstName} {t.secondName}
                    </td>
                    <td>{t.userPhoneNumber}</td>
                    <td>{fmtMoney(t.totalPrice)}</td>
                    <td>{statusLabel[t.status] || t.status}</td>
                    <td>{fmtDate(t.created)}</td>
                  </tr>
                ))}
                {visibleTransactions.length === 0 && (
                  <tr>
                    <td colSpan={7} className={styles.empty}>
                      Nema transakcija
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            <div className={styles.totalAmount}>
              Realizovan iznos transakcija: {fmtMoney(totalTransactionsAmount)}
            </div>
          </div>
        ) : currentBranchId === null ? (
          <div className={styles.empty}>
            Izaberite apoteku da biste videli loyalty kartice.
          </div>
        ) : loadingLoyalty ? (
          <div className={styles.empty}>Učitavanje...</div>
        ) : loyaltyError ? (
          <div className={styles.errorBanner}>
            <span>{loyaltyError}</span>
            <button onClick={() => setLoyaltyReloadKey((k) => k + 1)}>
              Pokušaj ponovo
            </button>
          </div>
        ) : (
          <div className={styles.tableWrap}>
            <table>
              <thead>
                <tr>
                  <th>Kod</th>
                  <th>Status</th>
                  <th>Ime</th>
                  <th>Prezime</th>
                  <th>Email</th>
                  <th>Telefon</th>
                  <th>Mobilni</th>
                  <th>Reg. poeni</th>
                  <th>Bonus poeni</th>
                  <th>Br. računa</th>
                  <th>Ukupno računa</th>
                  <th>Učlanjen</th>
                </tr>
              </thead>
              <tbody>
                {loyalty?.elements.map((c) => (
                  <tr key={c.id}>
                    <td>{c.cardCode}</td>
                    <td>{c.cardStatus}</td>
                    <td>{c.firstName}</td>
                    <td>{c.lastName}</td>
                    <td>{c.email}</td>
                    <td>{c.phone}</td>
                    <td>{c.mobile}</td>
                    <td>{c.regularPointsBalance}</td>
                    <td>{c.bonusPointsBalance}</td>
                    <td>{c.invoiceCount}</td>
                    <td>{fmtMoney(c.invoiceTotal)}</td>
                    <td>{fmtDate(c.joinedDate)}</td>
                  </tr>
                ))}
                {loyalty && loyalty.elements.length === 0 && (
                  <tr>
                    <td colSpan={12} className={styles.empty}>
                      Nema loyalty kartica
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            {loyalty && loyalty.totalNumOfPages > 1 && (
              <div className={styles.pagination}>
                <button
                  disabled={loyaltyPage === 0}
                  onClick={() => setLoyaltyPage((p) => Math.max(0, p - 1))}
                >
                  Prethodna
                </button>
                <span>
                  Strana {loyalty.pageNum + 1} od {loyalty.totalNumOfPages}{" "}
                  (ukupno {loyalty.totalNumOfElements})
                </span>
                <button
                  disabled={loyalty.pageNum + 1 >= loyalty.totalNumOfPages}
                  onClick={() => setLoyaltyPage((p) => p + 1)}
                >
                  Sledeća
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminMarketPlacePage;
